import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { validateTurkishContent, validateTurkishAddress } from '../../../utils/turkishValidation';
import { supabase } from '../../../lib/supabase';

const MARKETPLACE_BUCKET = 'marketplace-images';

const CreateListingModal = ({ isOpen, onClose, onSubmit, categoryOptions, conditionOptions, editListing }) => {
  const isEditMode = !!editListing;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    condition: '',
    price: '',
    location: '',
    contactInfo: '',
  });

  // New files selected by user (File objects)
  const [selectedFiles, setSelectedFiles] = useState([]);
  // Preview URLs for new files (blob URLs)
  const [newPreviewUrls, setNewPreviewUrls] = useState([]);
  // Existing image URLs from DB (edit mode)
  const [existingImageUrls, setExistingImageUrls] = useState([]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [toast, setToast] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && editListing) {
      setFormData({
        title: editListing?.title || '',
        description: editListing?.description || '',
        category: editListing?.category || '',
        condition: editListing?.condition || '',
        price: editListing?.price?.toString() || '',
        location: editListing?.location || '',
        contactInfo: editListing?.contact_info || '',
      });
      setExistingImageUrls(editListing?.images || []);
      setSelectedFiles([]);
      setNewPreviewUrls([]);
    } else if (!isEditMode) {
      setFormData({ title: '', description: '', category: '', condition: '', price: '', location: '', contactInfo: '' });
      setExistingImageUrls([]);
      setSelectedFiles([]);
      setNewPreviewUrls([]);
    }
    setErrors({});
    setUploadProgress('');
  }, [editListing, isEditMode, isOpen]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const totalExisting = existingImageUrls?.length;
    const currentNewCount = selectedFiles?.length;
    const remaining = 5 - totalExisting - currentNewCount;
    if (remaining <= 0) return;
    const addedFiles = Array.from(e?.target?.files || [])?.slice(0, remaining);
    const addedPreviews = addedFiles?.map(f => URL.createObjectURL(f));
    setSelectedFiles(prev => [...prev, ...addedFiles]);
    setNewPreviewUrls(prev => [...prev, ...addedPreviews]);
    if (errors?.images) setErrors(prev => ({ ...prev, images: '' }));
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemoveExistingImage = (idx) => {
    setExistingImageUrls(prev => prev?.filter((_, i) => i !== idx));
  };

  const handleRemoveNewImage = (idx) => {
    URL.revokeObjectURL(newPreviewUrls?.[idx]);
    setSelectedFiles(prev => prev?.filter((_, i) => i !== idx));
    setNewPreviewUrls(prev => prev?.filter((_, i) => i !== idx));
  };

  const totalImageCount = existingImageUrls?.length + selectedFiles?.length;

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Başlık gereklidir';
    } else {
      const titleError = validateTurkishContent(formData?.title, 'Başlık', 3);
      if (titleError) newErrors.title = titleError;
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Açıklama gereklidir';
    } else {
      const descError = validateTurkishContent(formData?.description, 'Açıklama', 10);
      if (descError) newErrors.description = descError;
    }

    if (!formData?.category) newErrors.category = 'Kategori seçiniz';
    if (!formData?.condition) newErrors.condition = 'Durum seçiniz';
    if (!formData?.price || formData?.price <= 0) newErrors.price = 'Geçerli bir fiyat giriniz';

    if (!formData?.location?.trim()) {
      newErrors.location = 'Konum gereklidir';
    } else {
      const locationError = validateTurkishAddress(formData?.location, 'Konum');
      if (locationError) newErrors.location = locationError;
    }

    if (totalImageCount === 0) {
      newErrors.images = 'En az 1 fotoğraf yükleyiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const uploadNewImages = async () => {
    if (selectedFiles?.length === 0) return [];

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Oturum açmanız gerekiyor');

    setUploadProgress(`${selectedFiles?.length} fotoğraf yükleniyor...`);

    const uploadPromises = selectedFiles?.map(async (file, i) => {
      const ext = file?.name?.split('.')?.pop();
      const filePath = `${user?.id}/${Date.now()}_${i}_${Math.random()?.toString(36)?.slice(2)}.${ext}`;

      const { error: uploadError } = await supabase?.storage
        ?.from(MARKETPLACE_BUCKET)
        ?.upload(filePath, file, { upsert: false });

      if (uploadError) {
        throw new Error(`Fotoğraf ${i + 1} yüklenemedi: ${uploadError.message}`);
      }

      const { data: urlData } = supabase?.storage
        ?.from(MARKETPLACE_BUCKET)
        ?.getPublicUrl(filePath);

      return urlData?.publicUrl;
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    setUploadProgress('');
    return uploadedUrls?.filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    setUploadProgress('Yükleniyor...');

    try {
      const newImageUrls = await uploadNewImages();

      // Combine existing (kept) images + newly uploaded images
      const allImageUrls = [...existingImageUrls, ...newImageUrls];

      setUploadProgress('İlan kaydediliyor...');

      await onSubmit({ ...formData, imageUrls: allImageUrls, editId: editListing?.id });

      showToast('success', isEditMode ? 'İlanınız başarıyla güncellendi!' : 'İlanınız başarıyla yayınlandı!');

      // Reset state
      setFormData({ title: '', description: '', category: '', condition: '', price: '', location: '', contactInfo: '' });
      setSelectedFiles([]);
      newPreviewUrls?.forEach(url => URL.revokeObjectURL(url));
      setNewPreviewUrls([]);
      setExistingImageUrls([]);
      setErrors({});
      setUploadProgress('');

      setTimeout(() => onClose(), 1200);
    } catch (err) {
      console.error('Failed to submit listing:', err);
      showToast('error', err?.message || 'İlan kaydedilemedi. Lütfen tekrar deneyin.');
      setErrors({ submit: err?.message || 'İlan kaydedilemedi' });
      setUploadProgress('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg max-w-sm transition-all ${
          toast?.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-green-50 border border-green-200 text-green-800'
        }`}>
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={20} className={toast?.type === 'error' ? 'text-red-500 flex-shrink-0' : 'text-green-500 flex-shrink-0'} />
          <p className="text-sm font-medium">{toast?.message}</p>
          <button onClick={() => setToast(null)} className="ml-auto">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}
      <div className="bg-card rounded-xl shadow-organic-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground flex items-center gap-2">
            <Icon name={isEditMode ? 'Edit' : 'Plus'} size={24} className="text-primary" />
            {isEditMode ? 'İlanı Düzenle' : 'Yeni İlan Oluştur'}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
            aria-label="Kapat"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">
            <Input
              label="İlan Başlığı"
              type="text"
              placeholder="Örn: Starboard SUP Tahta - 10'6"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Açıklama <span className="text-error">*</span>
              </label>
              <textarea
                className="w-full min-h-[120px] px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Ürününüz hakkında detaylı bilgi verin..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
              />
              {errors?.description && (
                <p className="text-sm text-error mt-1">{errors?.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Kategori"
                placeholder="Kategori seçin"
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => handleInputChange('category', value)}
                error={errors?.category}
                required
              />

              <Select
                label="Durum"
                placeholder="Durum seçin"
                options={conditionOptions}
                value={formData?.condition}
                onChange={(value) => handleInputChange('condition', value)}
                error={errors?.condition}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Fiyat (₺)"
                type="number"
                placeholder="0"
                value={formData?.price}
                onChange={(e) => handleInputChange('price', e?.target?.value)}
                error={errors?.price}
                min="0"
                required
              />

              <Input
                label="Konum"
                type="text"
                placeholder="Örn: İstanbul, Kadıköy"
                value={formData?.location}
                onChange={(e) => handleInputChange('location', e?.target?.value)}
                error={errors?.location}
                required
              />
            </div>

            <Input
              label="İletişim Bilgisi"
              type="text"
              placeholder="Email veya telefon numarası (opsiyonel)"
              value={formData?.contactInfo}
              onChange={(e) => handleInputChange('contactInfo', e?.target?.value)}
            />

            {/* Photo Upload Section */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Fotoğraflar <span className="text-error">*</span>
                <span className="ml-2 text-xs text-muted-foreground font-normal">({totalImageCount}/5)</span>
              </label>

              {/* Thumbnail Grid */}
              {(existingImageUrls?.length > 0 || newPreviewUrls?.length > 0) && (
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {/* Existing images (edit mode) */}
                  {existingImageUrls?.map((url, idx) => (
                    <div key={`existing-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                      <img src={url} alt={`Mevcut fotoğraf ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        aria-label="Fotoğrafı kaldır"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                  {/* New preview images */}
                  {newPreviewUrls?.map((url, idx) => (
                    <div key={`new-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-primary group">
                      <img src={url} alt={`Yeni fotoğraf ${idx + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-primary/70 text-white text-[9px] text-center py-0.5">Yeni</div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        aria-label="Fotoğrafı kaldır"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload button — only show if under 5 total */}
              {totalImageCount < 5 && (
                <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${errors?.images ? 'border-red-400 bg-red-50/30' : 'border-border'}`}>
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Icon name="Upload" size={22} />
                    <span className="text-sm font-medium">
                      {totalImageCount === 0 ? 'Fotoğraf seçmek için tıklayın' : 'Daha fazla fotoğraf ekle'}
                    </span>
                    <span className="text-xs">PNG, JPG, WEBP — En fazla {5 - totalImageCount} fotoğraf daha</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              )}

              {errors?.images && <p className="text-sm text-error mt-1">{errors?.images}</p>}
            </div>

            {uploadProgress && (
              <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 rounded-lg px-3 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                {uploadProgress}
              </div>
            )}

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3">
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">İlan İpuçları:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Net ve açıklayıcı başlık kullanın</li>
                    <li>Ürünün durumunu detaylı açıklayın</li>
                    <li>Kaliteli fotoğraflar ekleyin (maks. 5)</li>
                    <li>Gerçekçi fiyat belirleyin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            type="button"
            disabled={isSubmitting}
          >
            İptal
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName={isEditMode ? 'Save' : 'Check'}
            iconPosition="left"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Yükleniyor...' : (isEditMode ? 'Değişiklikleri Kaydet' : 'İlanı Yayınla')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateListingModal;